import { AlertTriangleIcon, CheckIcon, ChevronDownIcon, LoaderIcon, WrenchIcon } from 'lucide-react'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { cn } from '@/utils/cn'

export type ToolCallStatus = 'running' | 'complete' | 'error'

interface ToolCallHeaderProps {
  status: ToolCallStatus
  /** Tool icon rendered next to the status pill. Defaults to wrench. */
  icon?: React.ComponentType<{ className?: string }>
  /** Bold tool name (e.g. "list_entities", "search_documents"). */
  toolName: string
  /** Optional descriptor rendered after a "·" separator. */
  description?: React.ReactNode
  /** Optional duration in ms — renders as a pill on the right. */
  durationMs?: number
  /** Whether the body can be expanded (controls chevron + click). */
  expandable?: boolean
  open?: boolean
  onToggle?: () => void
}

function formatDuration(ms?: number): string | null {
  if (ms == null) return null
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

/**
 * Single-line, collapsible header used at the top of every agent tool-call card.
 * Status pill (check/spinner/alert) + tool icon + "**tool_name** · description"
 * + optional duration pill + chevron.
 */
export function ToolCallHeader({
  status,
  icon: Icon = WrenchIcon,
  toolName,
  description,
  durationMs,
  expandable = false,
  open = false,
  onToggle,
}: ToolCallHeaderProps) {
  const { t } = useTranslation()
  const duration = formatDuration(durationMs)
  // Translate the raw tool name (e.g. "find_entity_by_name") to a
  // human-readable label, falling back to the raw name for unmapped tools.
  const toolLabel = t(`chat.toolNames.${toolName}`, { defaultValue: toolName })

  return (
    <button
      type="button"
      onClick={() => expandable && onToggle?.()}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-3 py-3 text-[13px] md:gap-3 md:px-4',
        expandable ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default'
      )}
    >
      <span
        className={cn(
          'flex size-[18px] shrink-0 items-center justify-center rounded-full',
          status === 'complete' ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'
        )}
      >
        {status === 'running' ? (
          <LoaderIcon className="size-3 animate-spin" />
        ) : status === 'error' ? (
          <AlertTriangleIcon className="size-3" />
        ) : (
          <CheckIcon className="size-3" strokeWidth={3} />
        )}
      </span>

      <Icon className="size-4 shrink-0 text-muted-foreground" />

      {/* Phone: the name + description may take three lines; one truncated line
          would hide the description (the query) almost entirely, and two still
          cut a two-word query. */}
      <span className="grow text-left max-md:line-clamp-3 max-md:break-words md:truncate">
        <span className="font-semibold text-foreground">{toolLabel}</span>
        {description != null && (
          <>
            <span className="mx-1.5 text-muted-foreground">·</span>
            <span className="text-muted-foreground">{description}</span>
          </>
        )}
      </span>

      {duration && (
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 font-mono text-[10.5px] text-muted-foreground">
          {duration}
        </span>
      )}

      {expandable && (
        <ChevronDownIcon
          className={cn(
            'size-3.5 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180'
          )}
        />
      )}
    </button>
  )
}

/** Outer card shell that wraps the header + optional expanded body. */
export function ToolCallCard({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  // min-h reserves the single-line header's height up front so the card
  // doesn't reflow (and shift the viewport) when status/description arrive.
  return (
    <Card className={cn('my-4 min-h-[46px] w-full rounded-lg p-0', className)}>{children}</Card>
  )
}
